/// <amd-module name="Wit/WorkItemSignalR/WorkItemSignalRHub" />
import { IHubHostInformation, ISignalRHubOptions, SignalRHub } from "VSS/Features/SignalR/Hub";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History";
import { IWorkItemUpdatePayload } from "Wit/WorkItemSignalRTypes/WorkItemSignalRService.Types";

export interface ISignalROptions {
    hostInformation: IHubHostInformation;
    historyService: IVssHistoryService;
    pageContext: IVssPageContext;
    useSignalRAppPoolDefaultValue?: boolean;
}

interface IWorkItemHubClient {
    onWorkItemUpdated: (updatePayload: IWorkItemUpdatePayload[]) => void;
}

interface IWorkItemHubServer {
    watchWorkItem: (workItemId: number) => Promise<void>;
    unwatchWorkItem: (workItemId: number) => Promise<void>;
}

const WorkItemHubName = "workItemHub";
const UserInactivityTimeoutMs = 1 * 60 * 60 * 1000; // 1 hour
const CloseEmptyConnectionTimeoutInMS = 2 * 60 * 1000; // 2 min

function getProxyToRegister<C, S>(baseProxy: ISignalRHubProxy<any, any>): ISignalRHubProxy<C, S> {
    baseProxy.server = {
        unwatchWorkItem: function (workItemId: number) {
            return baseProxy.invoke.apply(baseProxy, ["UnwatchWorkItem", workItemId]);
        },
        watchWorkItem: function (workItemId: number) {
            return baseProxy.invoke.apply(baseProxy, ["WatchWorkItem", workItemId]);
        }
    };

    return baseProxy;
}

export class WorkItemHub extends SignalRHub<IWorkItemHubClient, IWorkItemHubServer> {
    private _callbacks: { [workItemId: number]: (updatePayload: IWorkItemUpdatePayload) => void } = {};
    private _disposed = false;
    private _timeoutHandle: number | null;

    constructor(options: ISignalROptions) {
        super({
            name: WorkItemHubName,
            hostInformation: options.hostInformation,
            userInactivityTimeoutMs: UserInactivityTimeoutMs,
            pageContext: options.pageContext,
            removeApiPath: true,
            useSignalRAppPoolDefaultValue: options.useSignalRAppPoolDefaultValue
        } as ISignalRHubOptions<IWorkItemHubClient, IWorkItemHubServer>);

        this.initializeProxy(getProxyToRegister, this._initializeClient);
    }

    public async watch(workItemId: number, onWorkItemUpdated: (updatePayload: IWorkItemUpdatePayload) => void) {
        this._clearTimeout();

        if (!this._disposed) {
            this._callbacks[workItemId] = onWorkItemUpdated;

            try {
                await this.start();
            } catch {
                // if the hub fails to start, dont log anything. This can happen if browser or internet proxy has disabled websockets
                delete this._callbacks[workItemId];
                this._tryStartTimeout();
                return;
            }

            try {
                const hub = this.getHub(WorkItemHubName);
                if (hub) {
                    await hub.server.watchWorkItem(workItemId);
                } else {
                    throw new Error("WorkItemHub not initialized");
                }
            } catch (e) {
                // if the hub fails to watch the work item, then remove it from the cache
                delete this._callbacks[workItemId];
                this._tryStartTimeout();
                throw e;
            }
        }
    }

    public async endWatch(workItemId: number) {
        this._clearTimeout();
        delete this._callbacks[workItemId];

        if (!this._disposed) {
            const hub = this.getHub(WorkItemHubName);
            if (hub) {
                await hub.server.unwatchWorkItem(workItemId);
            }

            this._tryStartTimeout();
        }
    }

    public dispose() {
        super.dispose();
        this._callbacks = {};
        this._clearTimeout();
        this._disposed = true;
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IWorkItemHubClient, IWorkItemHubServer>) => {
        proxy.client.onWorkItemUpdated = (updatePayload: IWorkItemUpdatePayload[]) => {
            updatePayload &&
                updatePayload.forEach(p => {
                    const callback = this._callbacks[p.id];
                    callback && callback(p);
                });
        };
    };

    private _tryStartTimeout() {
        // close any existing timeout before starting a new one
        this._clearTimeout();

        if (!this._disposed && Object.keys(this._callbacks).length === 0) {
            this._timeoutHandle = setTimeout(this._onTimeout, CloseEmptyConnectionTimeoutInMS);
        }
    }

    private _onTimeout = () => {
        if (!this._disposed && Object.keys(this._callbacks).length === 0) {
            this.stop(true);
        }
    };

    private _clearTimeout() {
        if (this._timeoutHandle) {
            clearTimeout(this._timeoutHandle);
            this._timeoutHandle = null;
        }
    }
}
